import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { TrainingPlan } from '../../types/trainingTypes';
import { parseLocalDateStr } from '../../utils/trainingUtils';
import '../../css/ManageEnrollmentModal.css';

interface ManageEnrollmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
    userName: string;
}

function formatDate(dateStr: string): string {
    const d = parseLocalDateStr(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function calculateDueDate(enrolledDate: string, cycleDuration: number): string {
    const d = parseLocalDateStr(enrolledDate);
    d.setDate(d.getDate() + cycleDuration);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * ManageEnrollmentModal — Per-user enrollment modal for managing which plans a user is in.
 *
 * Validates: Requirements 12.1, 12.2, 12.3, 12.4, 12.5, 12.6, 12.7
 */
export const ManageEnrollmentModal = ({
    isOpen,
    onClose,
    userId,
    userName,
}: ManageEnrollmentModalProps) => {
    const { plans, enrollments, enrollUser, unenrollUser } = useSafetyTrainingContext();

    // Current enrollments for this user
    const userEnrollments = useMemo(
        () => enrollments.filter(e => e.userId === userId),
        [enrollments, userId],
    );

    // Local toggle state: planId -> enrolled boolean
    const [toggleState, setToggleState] = useState<Record<string, boolean>>({});
    const [isSaving, setIsSaving] = useState(false);

    // Initialize toggle state from current enrollments
    useEffect(() => {
        if (!isOpen) return;
        const state: Record<string, boolean> = {};
        plans.forEach(plan => {
            state[plan.id] = userEnrollments.some(e => e.planId === plan.id);
        });
        setToggleState(state);
    }, [isOpen, plans, userEnrollments]);

    const handleToggle = useCallback((planId: string) => {
        setToggleState(prev => ({ ...prev, [planId]: !prev[planId] }));
    }, []);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            const promises: Promise<unknown>[] = [];

            plans.forEach(plan => {
                // Skip completed plans — they're read-only
                if (plan.status === 'COMPLETED') return;

                const wasEnrolled = userEnrollments.some(e => e.planId === plan.id);
                const isNowEnrolled = toggleState[plan.id] ?? false;

                if (!wasEnrolled && isNowEnrolled) {
                    promises.push(enrollUser(userId, plan.id));
                } else if (wasEnrolled && !isNowEnrolled) {
                    const enrollment = userEnrollments.find(e => e.planId === plan.id);
                    if (enrollment) {
                        promises.push(unenrollUser(enrollment.id));
                    }
                }
            });

            await Promise.all(promises);
            onClose();
        } catch (error) {
            console.error('Failed to save enrollment changes:', error);
        } finally {
            setIsSaving(false);
        }
    }, [plans, userEnrollments, toggleState, enrollUser, unenrollUser, userId, onClose]);

    const getPlanRowClass = (plan: TrainingPlan): string => {
        switch (plan.status) {
            case 'ACTIVE': return 'mem-plan-row active-plan';
            case 'DRAFT': return 'mem-plan-row draft-plan';
            case 'COMPLETED': return 'mem-plan-row completed-plan';
            default: return 'mem-plan-row';
        }
    };

    const getBadgeClass = (status: string): string => {
        switch (status) {
            case 'ACTIVE': return 'mem-plan-badge active';
            case 'DRAFT': return 'mem-plan-badge draft';
            case 'COMPLETED': return 'mem-plan-badge completed';
            default: return 'mem-plan-badge';
        }
    };

    const renderPlanMeta = (plan: TrainingPlan) => {
        const enrollment = userEnrollments.find(e => e.planId === plan.id);

        if (plan.status === 'ACTIVE' && enrollment) {
            return (
                <p className="mem-plan-meta enrolled-info">
                    Enrolled since {formatDate(enrollment.enrolledDate)}
                    {' · '}Due {calculateDueDate(enrollment.enrolledDate, plan.cycleDuration)}
                </p>
            );
        }

        if (plan.status === 'DRAFT') {
            return <p className="mem-plan-meta draft-info">Draft plan — not yet active</p>;
        }

        if (plan.status === 'COMPLETED') {
            return (
                <p className="mem-plan-meta completed-info">
                    Completed{plan.updatedAt ? ` ${formatDate(plan.updatedAt)}` : ''} — read only
                </p>
            );
        }

        return (
            <p className="mem-plan-meta">
                {plan.cycleDuration} days · {plan.minimumTimeRequired} min
            </p>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="mem-overlay" onClick={onClose}>
            <div className="mem-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="mem-header-wrapper">
                    <div className="mem-header">
                        <h3 className="mem-header-title">
                            Manage Enrollment — {userName}
                        </h3>
                        <p className="mem-note">
                            A user can be enrolled in multiple plans simultaneously. Each plan tracks its own completion cycle independently.
                        </p>
                    </div>
                    <button className="mem-header-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                {/* Plan list */}
                <div className="mem-plan-list">
                    {plans.length === 0 ? (
                        <div className="mem-empty">No training plans have been created yet.</div>
                    ) : (
                        plans.map(plan => {
                            const isEnrolled = toggleState[plan.id] ?? false;
                            const isCompleted = plan.status === 'COMPLETED';

                            return (
                                <div key={plan.id} className={getPlanRowClass(plan)}>
                                    <div className="mem-plan-info">
                                        <p className="mem-plan-name">
                                            {plan.name}
                                            <span className={getBadgeClass(plan.status)}>
                                                {plan.status}
                                            </span>
                                        </p>
                                        {renderPlanMeta(plan)}
                                    </div>
                                    <label className="mem-toggle">
                                        <input
                                            type="checkbox"
                                            checked={isEnrolled}
                                            onChange={() => handleToggle(plan.id)}
                                            disabled={isCompleted}
                                        />
                                        <span className="mem-toggle-slider" />
                                    </label>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="mem-footer">
                    <button className="mem-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="mem-save-btn"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving…' : 'Save Enrollment'}
                    </button>
                </div>
            </div>
        </div>
    );
};
